import {
  runDiagnosticEngine,
  DiagnosticReport,
  CheckResult,
  getFixGuidance,
  getVendorUpdateGuidance,
  FixGuidance,
} from './diagnosticEngine';
import type { PCCData } from '../utils/PCCUtils';

// ---------------------------------------------------------------------------
// Free-tier whitelist — these are the ONLY 6 checks a free user ever sees.
// ---------------------------------------------------------------------------
const FREE_CHECK_IDS = new Set<string>([
  'SYS-001', // Operating System
  'SYS-005', // System Uptime
  'SYS-006', // System Architecture (32 / 64 bit)
  'RAM-001', // Total RAM
  'STO-001', // C: Drive Free Space
  'STA-001', // Startup Programs Count
]);

// ---------------------------------------------------------------------------
// Overview block — real values shown on BOTH free + pro reports so users see
// the exact same data the upload preview already showed them.
// ---------------------------------------------------------------------------
export interface ReportOverview {
  os: string;
  manufacturer: string;
  model: string;
  systemType: string;
  ramGB: number;
  diskTotalGB: number;
  diskFreeGB: number;
  uptimeDays: number;
  startupCount: number;
  serialNumber: string;
  processor: string;
}

function buildOverview(pcc: any): ReportOverview {
  const sys = pcc?.system || {};
  const ram = pcc?.ram || {};
  const drive = pcc?.storage?.mainDrive || {};
  const startup = pcc?.startup || {};

  return {
    os: sys.os || sys.os_edition || 'Windows',
    manufacturer: sys.manufacturer || 'Unknown',
    model: sys.model || 'Unknown',
    systemType: sys.systemType || sys.system_type || 'x64-based PC',
    ramGB: Number(sys.ramGB || ram.totalGB || ram.total_gb || 0),
    diskTotalGB: Number(drive.sizeGB || drive.totalGB || drive.total_gb || 0),
    diskFreeGB: Number(drive.freeGB || drive.free_gb || 0),
    uptimeDays: Number(sys.uptimeDays || sys.uptime_days || 0),
    startupCount: Number(
      pcc?.startupCount ||
      startup.program_count ||
      (Array.isArray(startup.items) ? startup.items.length : 0) ||
      0
    ),
    serialNumber: sys.serialNumber || '',
    processor: sys.processor || sys.cpu?.model || '',
  };
}

// ---------------------------------------------------------------------------
// Adapter: PCCData (camelCase) → ParsedScanData (snake_case)
// runDiagnosticEngine reads snake-case paths; without this adapter every
// safeGet() returns the default, which is why the Free preview shows
// "Unknown" / 0 even though the upload preview shows real values.
// ---------------------------------------------------------------------------
function toEngineInput(pcc: any): any {
  const sys = pcc?.system || {};
  const ram = pcc?.ram || {};
  const drive = pcc?.storage?.mainDrive || {};
  const security = pcc?.security || {};
  const windows = pcc?.windows || {};
  const stability = pcc?.stability || {};
  const drivers = pcc?.drivers || {};
  const battery = pcc?.battery || {};
  const network = pcc?.network || {};
  const startup = pcc?.startup || {};
  const cpu = pcc?.cpu || {};
  const extras = pcc?.proExtras || {};
  const xDefender = extras.defender || {};
  const xUpdate = extras.windowsUpdate || {};
  const xTop = extras.topProcesses || {};
  const xStartup = extras.startupApps || {};
  const xCleanup = extras.diskCleanup || {};

  const totalGB = Number(drive.sizeGB || drive.totalGB || drive.total_gb || 0);
  const freeGB = Number(drive.freeGB || drive.free_gb || 0);
  const usedPct = totalGB > 0 ? Math.round(((totalGB - freeGB) / totalGB) * 100) : 0;

  return {
    scanId: pcc?.scanId || 'SCAN',
    timestamp: pcc?.timestamp || new Date().toISOString(),
    valid: true,
    data: {
      system: {
        manufacturer: sys.manufacturer || 'Unknown',
        model: sys.model || 'Unknown',
        os_edition: sys.os || sys.os_edition || 'Unknown',
        os_build: String(sys.build || sys.os_build || '0'),
        uptime_days: Number(sys.uptimeDays || sys.uptime_days || 0),
        system_type: sys.systemType || sys.system_type || 'x64-based PC',
        tpm: security.tpm || sys.tpm || 'Unknown',
        secure_boot: security.secureBoot || sys.secure_boot || 'Unknown',
      },
      cpu: {
        model: cpu.model || sys.processor || 'Unknown',
        cores: Number(cpu.cores || 0),
        load_pct: Number(cpu.loadPercent || cpu.load_pct || 0),
        max_speed_ghz: Number(cpu.clockSpeed || cpu.max_speed_ghz || 0),
        top_processes:
          (xTop.top_cpu && xTop.top_cpu.length
            ? xTop.top_cpu.map((p: any) => ({ name: p.name, cpu_sec: p.cpu_sec ?? 0 }))
            : (cpu.topProcesses || cpu.top_processes || []).map((p: any) => ({
                name: p.name,
                cpu_sec: p.cpu_sec ?? p.cpu ?? 0,
              }))),
      },
      ram: {
        total_gb: Number(sys.ramGB || ram.totalGB || ram.total_gb || 0),
        used_pct: Number(ram.usedPercent || ram.used_pct || 0),
        available_gb: Number(ram.availableGB || ram.available_gb || 0),
        slots_used: Number(ram.slotsUsed || ram.slots_used || 0),
        slots_total: Number(pcc?.ramSlotsTotal || ram.slots_total || 0),
        slots_free: Number(pcc?.ramSlotsFree || ram.slots_free || 0),
        top_processes:
          (xTop.top_ram && xTop.top_ram.length
            ? xTop.top_ram.map((p: any) => ({ name: p.name, ram_mb: p.ram_mb ?? 0 }))
            : (ram.topProcesses || ram.top_processes || []).map((p: any) => ({
                name: p.name,
                ram_mb: p.ram_mb ?? p.memMB ?? 0,
              }))),
      },
      storage: {
        volumes: [
          {
            used_pct: usedPct,
            free_gb: freeGB,
            total_gb: totalGB,
          },
        ],
        physical_disks: [
          {
            type: drive.type || 'SSD',
            health: drive.status || 'Healthy',
          },
        ],
        bitlocker: pcc?.storage?.bitLocker || security.bitLocker || 'Unknown',
        disk_error_count: Number(pcc?.storage?.diskErrors || 0),
        temp_size_gb: Number(
          xCleanup.temp_user_gb != null
            ? (xCleanup.temp_user_gb + (xCleanup.temp_windows_gb || 0))
            : (pcc?.tempSizeGB || 0)
        ),
        recycle_bin_gb: Number(xCleanup.recycle_bin_gb ?? pcc?.recycleBinSizeGB ?? 0),
      },
      battery: {
        present: !!(
          battery.present ||
          battery.fullChargeCapacity > 0 ||
          battery.wearPercent ||
          (battery.acStatus && battery.acStatus !== 'Unknown' && battery.acStatus !== 'Not Detected') ||
          (battery.status && battery.status !== 'Not Detected' && battery.status !== 'Unknown' && battery.status !== 'Battery Data Not Available')
        ),
        wear_pct: Number(battery.wearPercent || battery.wear_pct || 0),
        charge_pct: Number(battery.chargePercent ?? battery.charge_pct ?? 0),
        status: battery.status || 'Unknown',
        power_plan: pcc?.powerPlan || battery.power_plan || 'Balanced',
        design_capacity: Number(battery.designCapacity || 0),
        full_charge_capacity: Number(battery.fullChargeCapacity || 0),
        cycle_count:
          battery.cycleCount != null && Number(battery.cycleCount) >= 0
            ? Number(battery.cycleCount)
            : -1,
      },
      network: {
        adapter: network.adapter || '',
        wifi: network.wifi
          ? {
              signal_pct: network.wifi.signal ?? network.wifi.signal_pct ?? 0,
              link_speed_mbps: network.wifi.link_speed_mbps ?? 0,
              band: network.wifi.band ?? '',
            }
          : undefined,
        disconnects_7d: Number(network.recentDisconnects || network.disconnects_7d || 0),
        gateway_reachable: network.gatewayReachability ?? network.gateway_reachable ?? true,
      },
      startup: {
        program_count: Number(
          xStartup.count ??
          pcc?.startupCount ??
          startup.program_count ??
          (Array.isArray(startup.items) ? startup.items.length : 0)
        ),
        boot_time_seconds: Number(pcc?.bootTimeSec || startup.boot_time_seconds || 0),
        programs: xStartup.items || startup.items || startup.programs || [],
        non_ms_service_count: Number(startup.nonMicrosoftServices || startup.non_ms_service_count || 0),
      },
      drivers: {
        problem_count: Number((drivers.problemDevices || []).length || drivers.problem_count || 0),
        outdated_count: Number(drivers.outdatedCount || drivers.outdated_count || 0),
        usb_count: Number(drivers.usb_count || 0),
        unsigned_count: Number(drivers.unsignedCount || drivers.unsigned_count || 0),
      },
      windows_health: {
        last_update_installed: xUpdate.last_install_date || windows.lastUpdate || windows.last_update_installed || '',
        pending_reboot: !!(xUpdate.pending_reboot ?? windows.pendingReboot),
        activation: windows.activation || 'Unknown',
        firewall_enabled: security.firewall === 'Enabled' || security.firewall === true,
        defender_signature_age_days: Number(
          xDefender.signature_age_days ?? pcc?.defenderSigAge ?? 0
        ),
        // -1 sentinel means the scanner did not report a count at all.
        antivirus_count:
          typeof xDefender.antivirus_count === 'number'
            ? xDefender.antivirus_count
            : -1,
      },
      stability: {
        bsod_count_90d: Number(stability.bsodCount || stability.bsod_count_90d || 0),
        kernel_power_41_count: Number(stability.unexpectedShutdowns || 0),
        app_crash_count_30d: Number(stability.appCrashes || stability.app_crash_count_30d || 0),
        minidump_count: Number(stability.minidumpCount || stability.minidump_count || 0),
        reliability_trend: { current: stability.reliabilityIndex || 8, old: 8, decline: 0 },
        thermal_events_count: Number(stability.thermal_events_count || 0),
      },
      security: {
        // Prefer the real Defender API response from the v2.4 scanner. Fall back
        // to the loose text parsing only when the JSON module is absent.
        defender_realtime:
          xDefender.realtime_enabled === true
            ? true
            : xDefender.realtime_enabled === false
              ? false
              : ((security.defender || '').toLowerCase().includes('active') ||
                 (security.defender || '').toLowerCase().includes('enabled')),
        defender_enabled:
          xDefender.antivirus_enabled === true
            ? true
            : xDefender.antivirus_enabled === false
              ? false
              : !!xDefender.defender_present,
        uac_level: Number(security.uacLevel || 3),
        smartscreen: security.smartscreen || 'On',
      },
    },
  };
}

// ---------------------------------------------------------------------------
// Public entrypoint used by ReportView.tsx
// ---------------------------------------------------------------------------
export async function generateReport(
  parsedScanData: any,
  userTier: 'free' | 'pro'
): Promise<any> {
  const engineInput = toEngineInput(parsedScanData);
  const fullReport: DiagnosticReport = runDiagnosticEngine(engineInput);
  const overview = buildOverview(parsedScanData);

  // Make sure manufacturer/model on the report always reflect real upload data.
  fullReport.manufacturer = overview.manufacturer;
  fullReport.model = overview.model;

  if (userTier === 'free') {
    const freeChecks: CheckResult[] = fullReport.allChecks.filter(c => FREE_CHECK_IDS.has(c.id));
    const passCount = freeChecks.filter(c => c.status === 'pass').length;
    const warningCount = freeChecks.filter(c => c.status === 'warning').length;
    const criticalCount = freeChecks.filter(c => c.status === 'fail').length;
    const notDetectedCount = freeChecks.filter(c => c.status === 'not_detected').length;

    // Basic health score from the 6 free checks only
    const evaluated = freeChecks.length - notDetectedCount;
    const passWeight = evaluated > 0 ? Math.round((passCount / evaluated) * 100) : 100;
    // Soften with warnings (half credit) and criticals (zero credit)
    const softScore =
      evaluated > 0
        ? Math.round(((passCount + warningCount * 0.5) / evaluated) * 100)
        : passWeight;

    let scoreStatus: DiagnosticReport['scoreStatus'] = 'good';
    if (softScore < 40) scoreStatus = 'critical';
    else if (softScore < 60) scoreStatus = 'poor';
    else if (softScore < 75) scoreStatus = 'fair';
    else if (softScore < 90) scoreStatus = 'good';
    else scoreStatus = 'excellent';

    return {
      ...fullReport,
      tier: 'free',
      overview,
      allChecks: freeChecks,
      criticalFindings: freeChecks.filter(c => c.status === 'fail'),
      warningFindings: freeChecks.filter(c => c.status === 'warning'),
      passedChecks: freeChecks.filter(c => c.status === 'pass'),
      passCount,
      warningCount,
      criticalCount,
      notDetectedCount,
      totalChecks: 6,
      lockedChecksCount: 74,
      healthScore: softScore,
      scoreStatus,
      // Strip Pro-only sections
      categoryScores: [],
      fixPlan: [],
      primaryBottleneck: 'LOCKED',
      bottleneckExplanation: 'Upgrade to Pro to identify your system bottleneck.',
    };
  }

  // Pro tier — attach plain-English fix guidance to every actionable finding
  // so the report no longer falls back to "No action required." for warnings
  // and criticals.
  const attachGuidance = (c: CheckResult): CheckResult & { guidance?: FixGuidance } => {
    if (c.status !== 'warning' && c.status !== 'fail') return c;
    const g = getFixGuidance(c.id);
    return g ? { ...c, guidance: g } : c;
  };

  const enrichedCritical = (fullReport.criticalFindings || []).map(attachGuidance);
  const enrichedWarning = (fullReport.warningFindings || []).map(attachGuidance);
  const enrichedAll = (fullReport.allChecks || []).map(attachGuidance);

  return {
    ...fullReport,
    tier: 'pro',
    overview,
    lockedChecksCount: 0,
    criticalFindings: enrichedCritical,
    warningFindings: enrichedWarning,
    allChecks: enrichedAll,
    vendorUpdateGuidance: getVendorUpdateGuidance(overview.manufacturer),
    proExtras: parsedScanData?.proExtras || null,
    battery: parsedScanData?.battery || null,
  };
}

// ---------------------------------------------------------------------------
// Legacy types & class kept intact for existing imports elsewhere.
// ---------------------------------------------------------------------------
export interface SystemData {
  os: string;
  version: string;
  build: string;
  manufacturer: string;
  model: string;
  serialNumber: string;
  processor: string;
  ramGB: number;
  biosVersion: string;
  uptimeDays: number;
  installDate: string;
  scannerType?: string;
  cpu?: any;
  ram?: any;
  storage: any;
  battery?: any;
  network?: any;
  security: any;
  windows: any;
  stability: any;
  drivers: any;
  software?: any;
  powerPlan?: string;
  startupCount?: number;
  tempSizeGB?: number;
  recycleBinSizeGB?: number;
  defenderSigAge?: number;
  ramSlotsFree?: number;
  ramSlotsTotal?: number;
  bootTimeSec?: number;
  rawModules?: any;
}

export type Severity = 'high' | 'medium' | 'low' | 'info';
export type Confidence = 'high' | 'likely' | 'possible';
export type Risk = 'safe' | 'restart' | 'advanced' | 'technician';

export interface Finding {
  id: string;
  title: string;
  severity: Severity;
  confidence: Confidence;
  risk: Risk;
  isPro?: boolean;
  category: 'performance' | 'security' | 'hardware' | 'software' | 'forensics';
  description: string;
  whyItMatters: string;
  evidence: string;
  remediation: {
    steps: string;
    outcome: string;
    advanced?: string;
    technician?: string;
    avoid: string;
  };
}

export interface VendorStep { label: string; action?: string; }
export interface VendorGuidance {
  manufacturer: string;
  serviceTagHelp: string[];
  steps: VendorStep[];
  toolLink?: string;
}

export interface ReportResult {
  score: number;
  findings: Finding[];
  vendorGuidance: VendorGuidance | null;
  summary: string;
  systemData: SystemData;
  aiAnalysis?: string;
}

// Stub kept for backward compatibility; no callers in the new flow.
export class ReportEngine {
  static analyze(_data: SystemData): ReportResult {
    return {
      score: 100,
      findings: [],
      vendorGuidance: null,
      summary: 'Legacy ReportEngine.analyze() is deprecated. Use generateReport().',
      systemData: _data,
    };
  }
}
